import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { appModes, getAppMode, type AppMode } from '@/lib/appMode';
import {
  getTelegramInitData,
  getTelegramSafeAreaInsets,
  getTelegramThemeParams,
  getTelegramUser,
  getTelegramViewport,
  getTelegramWebApp,
  initializeTelegramWebApp,
  type TelegramColorScheme,
  type TelegramSafeAreaInset,
  type TelegramThemeParams,
  type TelegramWebApp,
  type TelegramWebAppUser,
} from '@/lib/telegram';

export interface AppEnvironmentValue {
  mode: AppMode;
  isTelegram: boolean;
  telegram: {
    webApp: TelegramWebApp | null;
    initData: string;
    user: TelegramWebAppUser | null;
    themeParams: TelegramThemeParams;
    colorScheme: TelegramColorScheme | null;
    platform: string | null;
    version: string | null;
    viewportHeight: number | null;
    viewportStableHeight: number | null;
    isExpanded: boolean;
    safeAreaInset: TelegramSafeAreaInset;
    contentSafeAreaInset: TelegramSafeAreaInset;
  };
}

const AppEnvironmentContext = createContext<AppEnvironmentValue | null>(null);

function setCssVariable(name: string, value: string | undefined) {
  if (!value) {
    document.documentElement.style.removeProperty(name);
    return;
  }
  document.documentElement.style.setProperty(name, value);
}

function applyEnvironmentToDocument(environment: AppEnvironmentValue) {
  document.documentElement.dataset.appMode = environment.mode;

  if (environment.telegram.colorScheme) {
    document.documentElement.dataset.telegramColorScheme = environment.telegram.colorScheme;
  } else {
    delete document.documentElement.dataset.telegramColorScheme;
  }

  const theme = environment.telegram.themeParams;
  setCssVariable('--pchelovod-tg-bg-color', theme.bg_color);
  setCssVariable('--pchelovod-tg-text-color', theme.text_color);
  setCssVariable('--pchelovod-tg-hint-color', theme.hint_color);
  setCssVariable('--pchelovod-tg-button-color', theme.button_color);
  setCssVariable('--pchelovod-tg-button-text-color', theme.button_text_color);
  setCssVariable('--pchelovod-tg-secondary-bg-color', theme.secondary_bg_color);

  const { safeAreaInset, contentSafeAreaInset } = environment.telegram;
  setCssVariable('--pchelovod-tg-safe-area-inset-top', `${safeAreaInset.top}px`);
  setCssVariable('--pchelovod-tg-content-safe-area-inset-top', `${contentSafeAreaInset.top}px`);
}

function readEnvironment(): AppEnvironmentValue {
  const webApp = getTelegramWebApp();
  const viewport = getTelegramViewport();
  const mode = getAppMode();
  const { safeArea, contentSafeArea } = getTelegramSafeAreaInsets();

  return {
    mode,
    isTelegram: mode === appModes.telegram,
    telegram: {
      webApp,
      initData: getTelegramInitData(),
      user: getTelegramUser(),
      themeParams: getTelegramThemeParams(),
      colorScheme: webApp?.colorScheme ?? null,
      platform: webApp?.platform ?? null,
      version: webApp?.version ?? null,
      viewportHeight: viewport.height,
      viewportStableHeight: viewport.stableHeight,
      isExpanded: viewport.isExpanded,
      safeAreaInset: safeArea,
      contentSafeAreaInset: contentSafeArea,
    },
  };
}

export function AppEnvironmentProvider({ children }: { children: ReactNode }) {
  const [environment, setEnvironment] = useState<AppEnvironmentValue>(() => readEnvironment());

  useEffect(() => {
    applyEnvironmentToDocument(environment);
  }, [environment]);

  useEffect(() => {
    const webApp = initializeTelegramWebApp({ expand: true });
    if (!webApp) return;

    const syncEnvironment = () => {
      setEnvironment(readEnvironment());
    };

    syncEnvironment();
    webApp.onEvent('themeChanged', syncEnvironment);
    webApp.onEvent('viewportChanged', syncEnvironment);
    webApp.onEvent('safeAreaChanged', syncEnvironment);
    webApp.onEvent('contentSafeAreaChanged', syncEnvironment);

    return () => {
      webApp.offEvent('themeChanged', syncEnvironment);
      webApp.offEvent('viewportChanged', syncEnvironment);
      webApp.offEvent('safeAreaChanged', syncEnvironment);
      webApp.offEvent('contentSafeAreaChanged', syncEnvironment);
    };
  }, []);

  return (
    <AppEnvironmentContext.Provider value={environment}>
      {children}
    </AppEnvironmentContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAppEnvironment() {
  const context = useContext(AppEnvironmentContext);
  if (!context) {
    throw new Error('useAppEnvironment must be used within AppEnvironmentProvider');
  }
  return context;
}
